<template>
    <div
        v-if="visible"
        class="ppv2-fixed ppv2-inset-0 ppv2-flex ppv2-flex-col ppv2-items-center ppv2-z-[9999] ppv2-py-10 ppv2-pt-[65px]"
        :class="[{ 'xl:ppv2-pl-[250px]': !isInPopup }, noScroll ? 'ppv2-overflow-hidden' : 'ppv2-overflow-y-auto']"
        style="background-color: rgba(0, 0, 0, 0.7) !important"
        @click.self="close"
    >
        <div
            ref="popupContainer"
            :style="{
                width: width || (isInPopup ? '30%' : '70%'),
                maxWidth: width || (isInPopup ? '30%' : '780px'),
                height: height || undefined,
                minHeight: minHeight || undefined,
                borderRadius: normalizeSize(43, true),
                display: 'flex',
                flexDirection: 'column',
            }"
            class="CustomDialog ppv2-relative ppv2-flex ppv2-flex-col ppv2-border-[1.81px] ppv2-border-[#5667A1] ppv2-min-w-80 ppv2-my-auto ppv2-overflow-hidden"
            :class="[
                { '!ppv2-max-h-[50vh] isInPopup ppv2-min-w-[250px]': isInPopup },
                { 'md:ppv2-min-w-[450px] ppv2-min-w-[320px]': !isInPopup },
                heightClass,
            ]"
        >
            <!-- Header Fixe seulement -->
            <div
                v-if="!hideHeader"
                class="ppv2-relative headerFix ppv2-flex-shrink-0 ppv2-z-10 ppv2-transition-shadow ppv2-duration-300 ppv2-border-b ppv2-border-gray-200"
                :class="[bgHeaderClass, { 'header-scrolled': isScrolled }]"
                :style="{
                    borderTopLeftRadius: normalizeSize(43, true),
                    borderTopRightRadius: normalizeSize(43, true),
                    paddingTop: normalizeSizeVertical(30, true),
                    paddingBottom: normalizeSizeVertical(20, true),
                    paddingRight: normalizeSize(40, true),
                    paddingLeft: normalizeSize(40, true),
                }"
            >
                <div
                    v-if="defaultTitle || $slots.title"
                    class="ppv2-text-black poppinsSemiBold ppv2-pr-12"
                    :class="customClassTitle"
                    :style="{ fontSize: normalizeFontSize(18), lineHeight: '1.4' }"
                >
                    <slot name="title">{{ defaultTitle }}</slot>
                </div>
                <div
                    @click="close"
                    class="cursor-pointer ppv2-absolute ppv2-right-4 ppv2-top-[50%] ppv2-translate-y-[-50%] ppv2-z-50 ppv2-p-1.5 hover:ppv2-bg-gray-100 dark:hover:ppv2-bg-gray-700 ppv2-rounded-full ppv2-transition-colors"
                >
                    <X color="#505D92" :size="20" />
                </div>
            </div>

            <!-- Contenu scrollable -->
            <div
                class="ppv2-flex-1 custom-scrollbar ppv2-max-w-full"
                :class="noScroll ? 'ppv2-overflow-hidden' : 'ppv2-overflow-y-auto ppv2-overflow-x-hidden'"
                @scroll="handleScroll"
                :style="{
                    borderBottomLeftRadius: normalizeSize(40, true),
                    borderBottomRightRadius: normalizeSize(40, true),
                    paddingTop: noScroll ? '0' : normalizeSizeVertical(20, true),
                    paddingBottom:
                        contentPaddingBottom !== null
                            ? normalizeSizeVertical(contentPaddingBottom, true)
                            : noScroll
                              ? '0'
                              : normalizeSizeVertical(20, true),
                    paddingRight: noScroll ? '0' : normalizeSize(20, true),
                    paddingLeft: noScroll ? '0' : normalizeSize(20, true),
                    justifyContent: centerContent ? 'center' : 'unset',
                    display: noScroll ? 'flex' : 'block',
                    flexDirection: noScroll ? 'column' : 'unset',
                }"
            >
                <div v-if="$slots.btn_toggle">
                    <slot name="btn_toggle"></slot>
                </div>

                <div
                    v-if="$slots.content"
                    class="ppv2-block ppv2-max-w-full"
                    :class="[
                        { 'ppv2-px-2 ppv2-overflow-y-auto ppv2-overflow-x-hidden': !noScroll },
                        noScroll ? 'ppv2-flex-1 ppv2-overflow-hidden' : '',
                    ]"
                    :style="{ display: noScroll ? 'flex' : 'block', flexDirection: noScroll ? 'column' : 'unset' }"
                >
                    <slot name="content"></slot>
                </div>
            </div>

            <!-- Footer Fixe -->
            <div
                v-if="showFooter()"
                class="ppv2-sticky ppv2-bottom-0 ppv2-w-full ppv2-z-10 ppv2-bg-transparent ppv2-pointer-events-none"
                :style="{
                    padding: normalizeSizeVertical(25, true),
                    borderBottomLeftRadius: normalizeSize(43, true),
                    borderBottomRightRadius: normalizeSize(43, true),
                }"
            >
                <div
                    class="ppv2-flex md:ppv2-flex-row ppv2-flex-col ppv2-justify-center ppv2-pointer-events-auto"
                    :class="customClassActions"
                    :style="{ gap: normalizeSize(16, true) }"
                >
                    <slot name="actions">
                        <BaseButton
                            id="button"
                            type="button"
                            variant="primary"
                            @click="confirm"
                            customClass="!ppv2-min-h-[40px] !ppv2-h-[45px] !ppv2-min-w-[135px]"
                            :customHeight="43"
                        >
                            {{ $t('common.save') }}
                        </BaseButton>
                        <BaseButton
                            id="button"
                            type="button"
                            variant="outline"
                            @click="close"
                            customClass="!ppv2-min-h-[40px] !ppv2-h-[45px] !ppv2-min-w-[135px] ppv2-bg-white dark:ppv2-bg-dark-popup"
                            :customHeight="43"
                        >
                            {{ $t('common.cancel') }}
                        </BaseButton>
                    </slot>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import normalizeSize from '../../utils/normalizeSize'
import BaseButton from './BaseButton.vue'
import { X } from 'lucide-vue'
import normalizeSizeVertical from '../../utils/normalizeSizeVertical'
import normalizeFontSize from '../../utils/normalizeFontSize'

export default {
    name: 'CustomDialog',
    components: { X, BaseButton },
    props: {
        value: { type: Boolean, default: false },
        defaultTitle: { type: String, default: null },
        customClassTitle: { type: String, default: null },
        isInPopup: { type: Boolean, default: false },
        heightClass: { type: String, default: 'ppv2-max-h-[80vh]' },
        width: { type: String, default: null },
        height: { type: String, default: null },
        minHeight: { type: String, default: null },
        centerContent: { type: Boolean, default: false },
        bgHeaderClass: { type: String, default: 'bg-purple ppv2-bg-opacity-10' },
        hideHeader: { type: Boolean, default: false },
        noScroll: { type: Boolean, default: false },
        customClassActions: { type: String, default: null },
        hideFooter: { type: Boolean, default: false },
        contentPaddingBottom: { type: [String, Number], default: null },
    },
    data() {
        return {
            isScrolled: false,
        }
    },
    computed: {
        visible: {
            get() {
                return this.value
            },
            set(val) {
                this.$emit('input', val)
            },
        },
    },
    watch: {
        visible: {
            immediate: true,
            handler(val) {
                if (typeof document !== 'undefined') {
                    document.body.style.overflow = val ? 'hidden' : ''
                }
            },
        },
    },
    beforeDestroy() {
        if (typeof document !== 'undefined') {
            document.body.style.overflow = ''
        }
    },
    methods: {
        normalizeFontSize,
        normalizeSizeVertical,
        normalizeSize,
        // MÉTHODE et non `computed` : `$slots` n'est pas réactif, un computed figerait le résultat
        // du premier rendu. Ici la décision est reprise à chaque rendu, donc elle suit le slot.
        showFooter() {
            if (this.hideFooter) return false
            const actions = this.$slots.actions
            if (!actions) return true
            return actions.some((vnode) => this.isRenderableNode(vnode))
        },
        // Un vnode « vide » en Vue 2 = commentaire (`v-if` faux, ou composant asynchrone pas encore
        // résolu → `createAsyncPlaceholder`, `tag === undefined` et `text === ''`). Seul le second
        // finira par afficher quelque chose : on le reconnaît à `asyncFactory`.
        isRenderableNode(vnode) {
            if (!vnode) return false
            if (vnode.asyncFactory) return true
            if (vnode.tag !== undefined) return true
            return !!(vnode.text && vnode.text.trim() !== '')
        },
        close() {
            this.visible = false
            this.$emit('cancel')
        },
        confirm() {
            this.$emit('confirm')
        },
        handleScroll(event) {
            this.isScrolled = event.target.scrollTop > 10
        },
    },
}
</script>

<style scoped>
.overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
    background: rgba(0, 0, 0, 0.3);
}

.dialog {
    width: 780px;
    max-width: 90%;
    border-radius: 8px;
}

.CustomDialog {
    background-color: #fff;
    display: flex;
    flex-direction: column;
}

/* Assurer que le header reste visible pendant le scroll */
.headerFix {
    margin-bottom: 1rem;
}

/* Shadow effect when scrolled */
.header-scrolled {
    background: inherit;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.dark .header-scrolled {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
    border-bottom-color: #2b3362 !important;
}

.dark .headerFix {
    border-bottom-color: #2b3362 !important;
}

/* Custom scrollbar styling */
.custom-scrollbar::-webkit-scrollbar {
    width: 8px;
}

.custom-scrollbar::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #555;
}

.dark .custom-scrollbar::-webkit-scrollbar-track {
    background: #1a1a3e;
}

.dark .custom-scrollbar::-webkit-scrollbar-thumb {
    background: #4a4a6e;
}

.dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #5a5a7e;
}

/* Style pour le mode sombre */
.dark .CustomDialog {
    background-color: #141243;
    box-shadow:
        inset 0 0 36px #2b3362,
        0 14px 45px rgba(0, 0, 0, 0.5);
}

.dark .CustomDialog:before {
    content: '';
    /* Required for pseudo-elements */
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: url('/media/bo/backgounds/bgPopup.png');
    background-repeat: repeat;
    opacity: 0.02;
    z-index: -1;
}

.dark .isInPopup {
    background: #0b0933 !important;
}

/* Assurer que le contenu scrollable prend l'espace disponible */
.ppv2-overflow-y-auto {
    flex: 1;
    display: flex;
    flex-direction: column;
}
@media (max-width: 768px) {
    .CustomDialog {
        width: 90% !important;
        max-width: 90% !important;
        min-width: 90% !important;
    }
    .isInPopup {
        width: 85% !important;
        max-width: 85% !important;
        min-width: 85% !important;
    }
}
</style>
